import { useState } from "react";
import { createModel } from "hox";
import { TablePaginationConfig } from "antd/es/table/interface";
import { ICardcaseDetail, ICardcaseSearchFields, cardcaseService, ICardcaseFormFields, ICardcaseInsert, ICardcaseUpdate, ICardGroupDetail } from "./cardcaseService";
import useGlobalModel, { transPgToPagination } from "@/model/globalModel";
import { ISearchPage } from "@/types/AppTypes";
import { DEFAULT_PAGE_SIZE, DEFAULT_FIRST_PAGE_NUM, defaultMdCmParam } from "@/utils/constants";
import { globalPrompt } from "@/components/message";
import { getRandomStr } from "@/utils/utils";

interface ICardcaseDrawerType {
    visible: boolean,
    mode: "add" | "edit"
}

export const useCardcaseModel = createModel(function () {
    const { user, roleAuth } = useGlobalModel(({ user, roleAuth }) => [user, roleAuth]);
    //列表数据源
    const [dataSource, setDataSource] = useState<ICardcaseDetail[]>([]);
    //查询条件
    const [searchParam, setSearchParam] = useState<ICardcaseSearchFields>({});
    //分页参数
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    //表格多选id值
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    //列表查询
    const loadData = async (page?: ISearchPage, searchValue: ICardcaseSearchFields = searchParam) => {
        const searchParams = {
            ...searchValue,
            userEntityId: roleAuth.indexOf("ebc-root") > -1 ? undefined : user.key,  //门户分配名片管理员角色查询所有用户名片夹
            page: {
                pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
                pageNum: page?.pageNum ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
            }
        }
        let { retData } = await cardcaseService.page(searchParams);
        const { records, ...pg } = retData;
        setPgCfg(transPgToPagination(pg));
        setDataSource(records.map(item => ({ ...item, id: item.id ?? getRandomStr() })));
    }

    //设置搜索value
    const updateSearchParam = (updateParam: ICardcaseSearchFields) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }
    //重置搜索参数
    const resetSearchParam = () => {
        setSearchParam({});
        setSelectedRowKeys([]);
        loadData(initPgCfg, {});
    }

    /**
     * 详情
     */
    const initCardcaseDrawer: ICardcaseDrawerType = { visible: false, mode: "add" }
    const [cardcaseDrawer, setCardcaseDrawer] = useState<ICardcaseDrawerType>(initCardcaseDrawer);
    const setICardcaseDrawer = (str?: "add" | "edit") => setCardcaseDrawer(str ? { visible: true, mode: str } : initCardcaseDrawer);

    //根据id查询
    const getSelectedRowData = () => {
        let filters = dataSource.filter(el => selectedRowKeys.length > 0 ? el.id === selectedRowKeys[0] : false);
        return filters && filters.length > 0 ? { ...filters[0] } : {};
    };


    //编辑
    const editCardcase = async (val: ICardcaseUpdate) => {
        let { createTime, updateTime, ...params } = val;
        let { retData } = await cardcaseService.update({ ...params, userEntityId: user.key });
        loadData();
        setICardcaseDrawer();
        globalPrompt("message", { text: "业务配置成功", type: "success" });
        return retData;

    }
    //新增
    const addCardcase = async (val: ICardcaseInsert) => {
        let { retData } = await cardcaseService.insert({ ...val, userEntityId: user.key });
        loadData();
        setICardcaseDrawer();
        globalPrompt("message", { text: "新增成功", type: "success" })
        return retData;
    }

    //启用 禁用 api
    const disableCardcase = async () => {
        let { retData } = await cardcaseService.disable(dataSource.find(item => item.id === selectedRowKeys[0])?.cardcaseId ?? "");
        loadData();
        setSelectedRowKeys([]);
        retData ?
            globalPrompt("message", { text: "删除成功", type: "success" })
            : globalPrompt("message", { text: "操作失败", type: "error" });
    };

    //分组管理弹框
    const [groupVisible, setGroupVisible] = useState<boolean>(false);
    const [dataGroupSource, setDataGroupSource] = useState<ICardGroupDetail[]>([]);
    //分组列表查询
    const loadDataGroup = async () => {
        const searchParams = {
            userEntityId: user.key,
            page: {
                pageSize: -1,
                pageNum: 1,
            }
        }
        let { retData } = await cardcaseService.groupPage(searchParams);
        const { records, ...pg } = retData;
        setDataGroupSource(records.map(item => ({ ...item, id: item.id ?? getRandomStr() })));
    }
    //分组编辑 Api
    const insertCardGroup = async (val: ICardGroupDetail) => {
        let { retData } = await cardcaseService.groupInsert({ ...val, userEntityId: user.key });
        loadDataGroup();
        globalPrompt("message", { text: "添加成功", type: "success" });
        return retData;
    }
    //分组编辑 Api
    const editCardGroup = async (val: ICardGroupDetail) => {
        let { createTime, updateTime, ...params } = val;
        let { retData } = await cardcaseService.groupUpdate({ ...params, userEntityId: user.key });
        loadDataGroup();
        globalPrompt("message", { text: "修改成功", type: "success" });
        return retData;
    }

    return {
        /***field***/
        searchParam,
        dataSource,
        selectedRowKeys,
        pgCfg,
        /***method***/
        cardcaseDrawer, setICardcaseDrawer,
        getSelectedRowData,
        editCardcase,
        setDataSource,
        setSelectedRowKeys,
        resetSearchParam,
        updateSearchParam,
        loadData,
        disableCardcase,
        groupVisible, setGroupVisible,
        dataGroupSource, setDataGroupSource,
        loadDataGroup,
        editCardGroup,
        insertCardGroup,
        addCardcase
    }
});